/* eslint-disable react-hooks/exhaustive-deps */
import {AddItemCircleButton} from "components"
import {checkPermission, handleError} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel, useMutation, useVisible} from "hooks"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {leadBucketSettingsService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseDepartmentId} from "types/departments"
import {ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {getPermissionsByDepartment} from "../helper"
import styles from "./LeadBucket.module.css"
import {DuplicatedLeadNotificationRecipientForm} from "./parts/DuplicatedLeadNotificationRecipientForm"
import {DuplicatedLeadNotificationRecipientItem} from "types/settings/lead-bucket-settings"
import {useQuery} from "@tanstack/react-query"

type LeadBucketSettingsProps = KlassappTableProps & {}

const LeadBucketSettings: React.FC<LeadBucketSettingsProps> = ({
    page,
    total,
    data,
    pageSize,
    orderField,
    isHideMenuActions,
    menuActions,
    isLoading,
    dispatch,
    dispatchFunc,
    onChangeFields,
    onChangePage,
    onChangeRowPerPage,
    allFields,
    onUpdateRowData,
    onUpdateTableData,
    onClickSortColumn,
    onDraggableColumn,
    onChangeAllFields,
    isShowTableHeaderAction,
    tableHeaderActions
}) => {
    const [currentItem, setCurrentItem] = useState<DuplicatedLeadNotificationRecipientItem>()
    const model = useModel()
    const {t} = useTranslation(["common", "settings"])
    const notificationRecipientDetailForm = useVisible()
    const confirmationPopup = useVisible()

    const leadBucketSettingsData = useQuery({
        queryKey: ["lead-bucket-duplicate-settings"],
        queryFn: async () => {
            const data = await leadBucketSettingsService.listAdmissionsLeadBucket({
                range: {
                    page,
                    pageSize
                }
            })
            return data
        },
        onError: (err) => {
            handleError(err)
        },
        onSuccess({data, total}) {
            const newData = data.map((item: any) => {
                item.collegeHtml = renderCampusHtml(item)
                item.staffHtml = renderStaffHtml(item)
                return item
            })
            dispatch({data: newData, total})
        }
    })

    const onCancelConfirmPopupLeadBucket = () => {
        setCurrentItem(null)
        confirmationPopup.close()
    }

    const deleteMutation = useMutation(
        async () => {
            return await leadBucketSettingsService.deleteAdmissionsLeadBucket({
                campusId: currentItem.campusId,
                receiveNotificationsFor: currentItem.receiveNotificationsFor
            })
        },
        {
            onError: handleError,
            onSettled: onCancelConfirmPopupLeadBucket,
            onSuccess: () => {
                leadBucketSettingsData.refetch()
            }
        }
    )

    const onClickDelete = async (deletedItem) => {
        confirmationPopup.open()
        setCurrentItem(deletedItem)
    }

    const onClickEdit = (editItem) => {
        notificationRecipientDetailForm.open()
        setCurrentItem(editItem)
    }

    const onClickAddItem = () => {
        notificationRecipientDetailForm.open()
        setCurrentItem(null)
    }

    const dispatchActions = () => {
        dispatchFunc([
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete}
        ])
    }

    const renderCampusHtml = (item) => {
        return (
            <div className={styles.titleWrap}>
                <span className={styles.title}>{item.campusName ?? item.receiveNotificationsFor}</span>
            </div>
        )
    }
    const renderStaffHtml = (item) => {
        return (
            <div className={styles.titleWrap}>
                <span className={styles.title}>{item.users?.map((user) => user.name).join(", ")}</span>
            </div>
        )
    }

    const fields = [t("settings:settings.leadBucket.college"), t("settings:settings.leadBucket.staff")]
    const columns = [
        {
            title: t("settings:settings.leadBucket.college"),
            field: "collegeHtml",
            style: {minWidth: "160px", maxWidth: "160px"},
            sortable: true
        },
        {
            title: t("settings:settings.leadBucket.staff"),
            field: "staffHtml",
            style: {maxWidth: "100px"},
            sortable: true
        }
    ]

    const canShowActionIcon = () => {
        return checkPermission(getPermissionsByDepartment(BaseDepartmentId.Admissions), model)
    }

    const handleSubmitSuccessfully = () => {
        setCurrentItem(undefined)
        notificationRecipientDetailForm.close()
        leadBucketSettingsData.refetch()
    }

    useEffect(() => {
        dispatchActions()
    }, [])

    return (
        <div className={styles.wrapper}>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                menuActions={isHideMenuActions ? [] : menuActions}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onClickRowItem={() => {}}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onClickSortColumn={onClickSortColumn}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />
            {canShowActionIcon && !isLoading && <AddItemCircleButton onClick={onClickAddItem} />}
            <DuplicatedLeadNotificationRecipientForm
                open={notificationRecipientDetailForm.isVisible}
                onClose={() => notificationRecipientDetailForm.close()}
                item={currentItem}
                onSuccess={handleSubmitSuccessfully}
            />
            <ConfirmPopup
                isVisible={confirmationPopup.isVisible}
                title={t("common:confirmationPopup.deleteSingle", {title: "Lead Bucket Item"})}
                onClose={onCancelConfirmPopupLeadBucket}
                onConfirm={() => deleteMutation.mutate(true)}
                loading={deleteMutation.isLoading}
            />
        </div>
    )
}

export const LeadBucket = KlassappTableHOC(LeadBucketSettings)
